import React, { useEffect, useState } from "react";
import Link from "next/link";

/**
 * The retreat photo gallery.
 *
 * Mobile is the majority of traffic to /retreats, and this component was the
 * single most expensive thing on the page: 16 images picked at random from 91
 * originals (JPEGs up to 3.4MB), rendered through bare `<img>` with no
 * dimensions and no lazy loading, and then **re-fetched and re-shuffled every 30
 * seconds** — which pulled a fresh set of full-size photos for as long as the tab
 * stayed open, and made the grid visibly rearrange itself under the reader.
 *
 * Three changes, in order of how much they were costing:
 *   1. The 30s poll is gone. The photos on disk change when Niki adds some after
 *      a retreat, not every half minute; one fetch per page view is right, and a
 *      gallery that reshuffles while you look at it is a bug, not a feature.
 *   2. Every tile is `loading="lazy"` with explicit dimensions, so the ~14 below
 *      the fold cost nothing until scrolled to and the grid reserves its space
 *      instead of shifting layout as each image lands.
 *   3. The source images are now 900px webp (the folder went 54MB → 6MB), so a
 *      full gallery is a few hundred KB rather than ~12MB.
 *
 * `key` is the src rather than the array index: with a randomised order, index
 * keys made React reuse the wrong <img> and re-download images it already had.
 */
const GALLERY_SIZE = 16;

const ImageGallery: React.FC = () => {
  const [images, setImages] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;

    const pickRandom = (all: string[]) =>
      [...all].sort(() => 0.5 - Math.random()).slice(0, GALLERY_SIZE);

    (async () => {
      try {
        const response = await fetch("/api/images");
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        const validImages = data.filter((src: string) => /\.(png|jpe?g|gif|webp)$/i.test(src));
        if (!cancelled) setImages(pickRandom(validImages));
      } catch (error) {
        console.error("Error fetching images:", error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section className="bg-[var(--bg-surface-dk)] py-14 px-6">
      <div className="max-w-6xl mx-auto">
        <h2 className="text-2xl md:text-3xl font-black text-[var(--color-text-brand)] text-center mb-2">
          Retreat Photo Gallery
        </h2>
        <p className="text-sm text-center text-[var(--text-on-light-muted)] mb-2">
          A glimpse into our transformative retreats
        </p>
        <p className="text-sm text-center text-[var(--text-on-light-muted)] mb-8">
          Share your memories —{" "}
          <Link
            href="mailto:retreats@cforganicwellness.com"
            className="text-[var(--color-text-brand)] underline underline-offset-2 hover:opacity-70"
          >
            email us your photos
          </Link>
        </p>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {images.map((src, index) => (
            <div
              key={src}
              className="relative overflow-hidden rounded-xl aspect-square group shadow-sm"
            >
              <img
                src={src}
                alt={`Retreat moment ${index + 1}`}
                width={900}
                height={900}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300 rounded-xl" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ImageGallery;
